import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StaffSidebarLayout from './StaffSidebarLayout';
import axios from 'axios';

const StaffNotifications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); // all, booking, cancellation, message
  const [errorMsg, setErrorMsg] = useState('');

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // All professionals use unified professional role type
  const getRoleType = useCallback(() => {
    return 'professional';
  }, []);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg('Configuration error. Please contact support.');
      return null;
    }
    return baseURL;
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/notifications`, { headers });
      setNotifications(res.data.notifications || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem('ogestyle_token');
        navigate('/login');
      } else {
        setErrorMsg(err?.response?.data?.message || 'Unable to load notifications.');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/staff/notifications/${notificationId}/read`,
        {},
        { headers }
      );

      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif._id === notificationId ? { ...notif, read: true } : notif
        )
      );
    } catch (err) {
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/staff/notifications/mark-all-read`,
        {},
        { headers }
      );

      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    } catch (err) {
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Filter notifications by type
  const filteredNotifications = notifications.filter(notif => {
    if (filterType === 'all') return true;
    return notif.type === filterType;
  });

  // Get notification icon and color
  const getNotificationStyle = (type) => {
    switch (type) {
      case 'booking':
        return { icon: '📅', color: '#3b82f6', bg: '#dbeafe' };
      case 'cancellation':
        return { icon: '❌', color: '#ef4444', bg: '#fee2e2' };
      case 'message':
        return { icon: '💬', color: '#10b981', bg: '#d1fae5' };
      case 'review':
        return { icon: '⭐', color: '#f59e0b', bg: '#fef3c7' };
      default:
        return { icon: '🔔', color: '#6b7280', bg: '#f3f4f6' };
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <StaffSidebarLayout roleType={getRoleType()}>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <p>Loading notifications...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={getRoleType()}>
      <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: '1000px', margin: '0 auto', paddingBottom: isMobile ? '100px' : '60px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h1 style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                Notifications
                {unreadCount > 0 && (
                  <span style={{
                    marginLeft: '12px',
                    padding: '4px 12px',
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </h1>
              <p style={{ color: '#6b7280', fontSize: isMobile ? '13px' : '14px', margin: 0 }}>
                Stay updated with booking alerts and customer messages
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  padding: '8px 16px',
                  background: '#2d6da3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {errorMsg && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              color: '#991b1b',
              marginBottom: '16px'
            }}>
              {errorMsg}
            </div>
          )}

          {/* Filter Tabs */}
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
            {['all', 'booking', 'cancellation', 'message'].map(type => {
              const count = type === 'all' 
                ? notifications.length 
                : notifications.filter(n => n.type === type).length;
              
              return (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  style={{
                    padding: '8px 16px',
                    background: filterType === type ? '#2d6da3' : 'white',
                    color: filterType === type ? 'white' : '#6b7280',
                    border: `2px solid ${filterType === type ? '#2d6da3' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    textTransform: 'capitalize'
                  }}
                >
                  {type} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 20px',
            background: '#f9fafb',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔕</div>
            <h3 style={{ color: '#666', fontSize: '17px', marginBottom: '8px' }}>No notifications</h3>
            <p style={{ color: '#999', fontSize: '15px' }}>
              {filterType === 'all' 
                ? "You're all caught up!" 
                : `No ${filterType} notifications`}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredNotifications.map(notification => {
              const style = getNotificationStyle(notification.type);
              
              return (
                <div
                  key={notification._id}
                  onClick={() => {
                    if (!notification.read) markAsRead(notification._id);
                    if (notification.bookingId) {
                      navigate(`/dashboard/${getRoleType()}/bookings/${notification.bookingId}`);
                      return;
                    }
                    if (notification.actionUrl) {
                      navigate(notification.actionUrl);
                    }
                  }}
                  style={{
                    padding: '16px',
                    background: notification.read ? 'white' : style.bg,
                    border: `1px solid ${notification.read ? '#e5e7eb' : style.color}`,
                    borderLeft: `4px solid ${style.color}`,
                    borderRadius: '8px',
                    cursor: notification.bookingId || notification.actionUrl ? 'pointer' : 'default',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (notification.bookingId || notification.actionUrl) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{
                      fontSize: '28px',
                      lineHeight: '1',
                      flexShrink: 0
                    }}>
                      {style.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          margin: 0,
                          color: '#1f2937'
                        }}>
                          {notification.title}
                        </h4>
                        <span style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          flexShrink: 0,
                          marginLeft: '12px'
                        }}>
                          {formatTimeAgo(notification.createdAt)}
                        </span>
                      </div>
                      <p style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        margin: '0 0 8px 0',
                        lineHeight: '1.5'
                      }}>
                        {notification.message}
                      </p>
                      {notification.customerName && (
                        <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                          👤 {notification.customerName}
                        </div>
                      )}
                      {!notification.read && (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '12px',
                          color: style.color,
                          fontWeight: '600'
                        }}>
                          • New
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </StaffSidebarLayout>
  );
};

export default StaffNotifications;
